import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import portfolioService, { MetricValue, ValuationData, SectorBreakdownRow, SectorSecurity, MarketCapDistribution } from '../../services/portfolio';
import LoadingSpinner from '../ui/LoadingSpinner';
import InfoTooltip from '../ui/InfoTooltip';
import { useCurrency } from '../../contexts/CurrencyContext';
import { usePrivacy } from '../../contexts/PrivacyContext';

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="9" height="9" viewBox="0 0 10 10" fill="none"
    style={{ transition: 'transform 0.18s ease', transform: open ? 'rotate(90deg)' : 'none', flexShrink: 0 }}
  >
    <path d="M3 1.5L6.5 5L3 8.5" stroke="#a1a1aa" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CardHeading: React.FC<{ title: string; right?: React.ReactNode }> = ({ title, right }) => (
  <div className="d-flex justify-content-between align-items-baseline mb-3">
    <span className="fw-semibold" style={{ fontSize: '0.95rem', color: '#000537' }}>{title}</span>
    {right && <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>{right}</span>}
  </div>
);

interface Props {
  portfolioId: number;
  // Injected between the valuation metrics and the market-cap/sector cards so the
  // parent can interleave the risk + correlation row into this stack.
  riskRow?: React.ReactNode;
}

const pct = (v: number) => `${(v * 100).toFixed(0)}%`;


interface TileProps {
  title: string;
  tooltip: React.ReactNode;
  formatted: string | null;
  metric: MetricValue;
  equityWeight: number;
}

// One metric in the full-width valuation strip.
const ValuationCell: React.FC<{ tile: TileProps; first: boolean }> = ({ tile, first }) => {
  const [open, setOpen] = useState(false);
  const { title, tooltip, formatted, metric } = tile;
  return (
    <div style={{
      flex: '1 1 120px', minWidth: 120,
      padding: '4px 14px',
      borderLeft: first ? undefined : '1px solid #f4f4f5',
      position: 'relative', zIndex: open ? 20 : 1,
    }}>
      <div className="d-flex justify-content-between align-items-start" style={{ minHeight: '30px' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#a1a1aa', lineHeight: 1.25 }}>{title}</span>
        <InfoTooltip text={tooltip} onShow={setOpen} />
      </div>
      <div style={{
        fontSize: '1.3rem', fontWeight: 700,
        color: formatted ? '#000537' : '#a1a1aa',
        letterSpacing: '-0.02em', lineHeight: 1, marginTop: '6px',
      }}>
        {formatted ?? '—'}
      </div>
      <div style={{ height: '3px', background: '#f4f4f5', borderRadius: '2px', overflow: 'hidden', marginTop: '8px' }}>
        <div style={{ width: `${metric.coverage * 100}%`, height: '100%', background: '#000537', borderRadius: '2px', transition: 'width 0.4s ease' }} />
      </div>
      <div style={{ fontSize: '0.62rem', color: '#a1a1aa', marginTop: '3px' }}>
        {pct(metric.coverage)} · {metric.n}
      </div>
    </div>
  );
};

const ValuationGridCard: React.FC<{ tiles: TileProps[]; provenance?: React.ReactNode }> = ({ tiles, provenance }) => (
  <div className="card card-forest">
    <div className="card-body p-3 p-md-4">
      <CardHeading title="Valuation" right="look-through, equity book" />
      <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: '14px' }}>
        {tiles.map((tile, i) => <ValuationCell key={tile.title} tile={tile} first={i === 0} />)}
      </div>
      {provenance && (
        <div style={{ marginTop: '14px', paddingTop: '10px', borderTop: '1px solid #f4f4f5', fontSize: '0.72rem', color: '#a1a1aa' }}>
          {provenance}
        </div>
      )}
    </div>
  </div>
);

const COL = '1fr 96px 56px 52px 52px';

const SectorBreakdownCard: React.FC<{
  rows: SectorBreakdownRow[];
  portfolioEquityWeight: number;
  portfolioPE: number | null;
  portfolioPB: number | null;
}> = ({ rows, portfolioEquityWeight, portfolioPE, portfolioPB }) => {
  const { formatAmountCompact } = useCurrency();
  const { isPrivateMode } = usePrivacy();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (sector: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(sector) ? next.delete(sector) : next.add(sector);
      return next;
    });

  const portfolioValue = rows.reduce((s, r) => s + r.value, 0);

  const hdr: React.CSSProperties = {
    fontSize: '0.72rem', color: '#a1a1aa',
    paddingBottom: '10px', borderBottom: '1px solid #f4f4f5',
  };
  const cell = (extra?: React.CSSProperties): React.CSSProperties => ({
    fontSize: '0.82rem', padding: '8px 0',
    borderBottom: '1px solid #f9f9f9',
    ...extra,
  });

  return (
    <div className="card card-forest h-100">
      <div className="card-body p-3 p-md-4">
        <CardHeading title="By Sector" right="look-through, equity book" />
        <div style={{ display: 'grid', gridTemplateColumns: COL, gap: '0 16px', alignItems: 'center' }}>
              {/* Header */}
              <div style={hdr}>Sector</div>
              <div style={{ ...hdr, textAlign: 'right' }}>Value</div>
              <div style={{ ...hdr, textAlign: 'right' }}>Weight</div>
              <div style={{ ...hdr, textAlign: 'right' }}>P/E</div>
              <div style={{ ...hdr, textAlign: 'right' }}>P/B</div>

              {rows.map((row) => {
                const open = expanded.has(row.sector);
                const isLast = row === rows[rows.length - 1];
                const rowBorder = open || isLast ? 'none' : '1px solid #f9f9f9';
                const clickable = row.securities.length > 0;
                return (
                  <React.Fragment key={row.sector}>
                    {/* Sector header row */}
                    <div
                      onClick={() => clickable && toggle(row.sector)}
                      style={{ ...cell({ borderBottom: rowBorder, color: '#18181b', cursor: clickable ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '7px' }) }}
                    >
                      {clickable
                        ? <Chevron open={open} />
                        : <span style={{ width: '9px', flexShrink: 0 }} />}
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.sector}</span>
                    </div>
                    <div style={cell({ textAlign: 'right', color: '#52525b', fontVariantNumeric: 'tabular-nums', borderBottom: rowBorder })}>
                      {formatAmountCompact(row.value, isPrivateMode)}
                    </div>
                    <div style={cell({ textAlign: 'right', color: '#71717a', fontVariantNumeric: 'tabular-nums', borderBottom: rowBorder })}>
                      {(row.equity_weight * 100).toFixed(1)}%
                    </div>
                    <div style={cell({ textAlign: 'right', color: row.pe.value !== null ? '#18181b' : '#a1a1aa', fontVariantNumeric: 'tabular-nums', borderBottom: rowBorder })}>
                      {row.pe.value !== null ? `${row.pe.value.toFixed(1)}×` : '—'}
                    </div>
                    <div style={cell({ textAlign: 'right', color: row.pb.value !== null ? '#18181b' : '#a1a1aa', fontVariantNumeric: 'tabular-nums', borderBottom: rowBorder })}>
                      {row.pb.value !== null ? `${row.pb.value.toFixed(2)}×` : '—'}
                    </div>

                    {/* Expanded security list */}
                    {open && (
                      <div style={{ gridColumn: '1 / -1', background: '#fafafa', borderRadius: '6px', margin: '0 -4px 4px', padding: '4px 12px 8px', borderBottom: isLast ? 'none' : '1px solid #f9f9f9' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: COL, gap: '0 16px', alignItems: 'center' }}>
                          <div style={{ fontSize: '0.68rem', color: '#a1a1aa', padding: '6px 0 4px' }}>Security</div>
                          <div style={{ fontSize: '0.68rem', color: '#a1a1aa', textAlign: 'right', padding: '6px 0 4px' }}>Value</div>
                          <div style={{ fontSize: '0.68rem', color: '#a1a1aa', textAlign: 'right', padding: '6px 0 4px' }}>Weight</div>
                          <div style={{ fontSize: '0.68rem', color: '#a1a1aa', textAlign: 'right', padding: '6px 0 4px' }}>P/E</div>
                          <div style={{ fontSize: '0.68rem', color: '#a1a1aa', textAlign: 'right', padding: '6px 0 4px' }}>P/B</div>
                          {row.securities.map((sec: SectorSecurity, si: number) => {
                            const secBorder = si < row.securities.length - 1 ? '1px solid #f0f0f0' : 'none';
                            return (
                              <React.Fragment key={sec.identifier}>
                                <div style={{ fontSize: '0.78rem', color: '#52525b', padding: '5px 0', borderBottom: secBorder, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  title={sec.name || sec.identifier}>
                                  {sec.name || sec.identifier}
                                  <span style={{ marginLeft: '6px', fontSize: '0.68rem', color: '#a1a1aa' }}>{sec.identifier}</span>
                                </div>
                                <div style={{ fontSize: '0.78rem', color: '#71717a', textAlign: 'right', padding: '5px 0', borderBottom: secBorder, fontVariantNumeric: 'tabular-nums' }}>
                                  {formatAmountCompact(sec.value, isPrivateMode)}
                                </div>
                                <div style={{ fontSize: '0.78rem', color: '#71717a', textAlign: 'right', padding: '5px 0', borderBottom: secBorder, fontVariantNumeric: 'tabular-nums' }}>
                                  {(sec.weight * 100).toFixed(2)}%
                                </div>
                                <div style={{ fontSize: '0.78rem', color: sec.pe !== null ? '#18181b' : '#d4d4d8', textAlign: 'right', padding: '5px 0', borderBottom: secBorder, fontVariantNumeric: 'tabular-nums' }}>
                                  {sec.pe !== null ? `${sec.pe.toFixed(1)}×` : '—'}
                                </div>
                                <div style={{ fontSize: '0.78rem', color: sec.pb !== null ? '#18181b' : '#d4d4d8', textAlign: 'right', padding: '5px 0', borderBottom: secBorder, fontVariantNumeric: 'tabular-nums' }}>
                                  {sec.pb !== null ? `${sec.pb.toFixed(2)}×` : '—'}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Portfolio total */}
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#000537', padding: '10px 0 0', borderTop: '1px solid #f4f4f5' }}>Portfolio</div>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#000537', textAlign: 'right', padding: '10px 0 0', borderTop: '1px solid #f4f4f5', fontVariantNumeric: 'tabular-nums' }}>
                {formatAmountCompact(portfolioValue, isPrivateMode)}
              </div>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#000537', textAlign: 'right', padding: '10px 0 0', borderTop: '1px solid #f4f4f5', fontVariantNumeric: 'tabular-nums' }}>
                {pct(portfolioEquityWeight)}
              </div>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#000537', textAlign: 'right', padding: '10px 0 0', borderTop: '1px solid #f4f4f5', fontVariantNumeric: 'tabular-nums' }}>
                {portfolioPE !== null ? `${portfolioPE.toFixed(1)}×` : '—'}
              </div>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#000537', textAlign: 'right', padding: '10px 0 0', borderTop: '1px solid #f4f4f5', fontVariantNumeric: 'tabular-nums' }}>
                {portfolioPB !== null ? `${portfolioPB.toFixed(2)}×` : '—'}
              </div>
            </div>
          </div>
        </div>
  );
};

const ValuationSection: React.FC<Props> = ({ portfolioId, riskRow }) => {
  const { data, isLoading, isError } = useQuery<ValuationData>({
    queryKey: ['portfolio-valuation', portfolioId],
    queryFn: () => portfolioService.getPortfolioValuation(portfolioId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="row g-4 mb-4">
        {[0, 1, 2].map(i => (
          <div key={i} className="col-12 col-md-4">
            <div className="card card-forest" style={{ minHeight: 160 }}>
              <div className="card-body d-flex align-items-center justify-content-center">
                <LoadingSpinner size="sm" text="Fetching..." />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="card card-forest">
        <div className="card-body p-4" style={{ color: '#71717a', fontSize: '0.875rem' }}>
          Analytical service unavailable — valuation metrics could not be loaded.
        </div>
      </div>
    );
  }

  const { metrics, universe, source, as_of } = data;
  const asOf = new Date(as_of).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  const formula = (f: string) => (
    <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', background: '#f4f4f5', borderRadius: '4px', padding: '3px 6px', margin: '5px 0', color: '#18181b' }}>{f}</div>
  );

  const valuationTiles: TileProps[] = [
    {
      title: 'Blended P/E',
      tooltip: (<>
        <div>Harmonic-mean trailing P/E weighted by look-through position size.</div>
        {formula('P/E = Σwᵢ / Σ(wᵢ / PEᵢ)')}
        <div style={{ color: '#71717a' }}>Equivalent to total market value divided by total trailing earnings.</div>
      </>),
      formatted: metrics.blended_pe.value !== null ? `${metrics.blended_pe.value.toFixed(1)}×` : null,
      metric: metrics.blended_pe,
      equityWeight: universe.equity_weight,
    },
    {
      title: 'Forward P/E',
      tooltip: (<>
        <div>Harmonic-mean forward P/E using next-twelve-months analyst estimates.</div>
        {formula('FwdP/E = Σwᵢ / Σ(wᵢ / FwdPEᵢ)')}
        <div style={{ color: '#71717a' }}>Lower than trailing P/E implies expected earnings growth.</div>
      </>),
      formatted: metrics.forward_pe.value !== null ? `${metrics.forward_pe.value.toFixed(1)}×` : null,
      metric: metrics.forward_pe,
      equityWeight: universe.equity_weight,
    },
    {
      title: 'PEG Ratio',
      tooltip: (<>
        <div>Harmonic-mean PEG (P/E ÷ expected EPS growth rate) weighted by look-through size.</div>
        {formula('PEG = Σwᵢ / Σ(wᵢ / PEGᵢ)')}
        <div style={{ color: '#71717a' }}>PEG &lt; 1 suggests the portfolio may be under-valued relative to its growth.</div>
      </>),
      formatted: metrics.peg_ratio.value !== null ? `${metrics.peg_ratio.value.toFixed(2)}×` : null,
      metric: metrics.peg_ratio,
      equityWeight: universe.equity_weight,
    },
  ];

  const balanceSheetTiles: TileProps[] = [
    {
      title: 'Blended P/B',
      tooltip: (<>
        <div>Harmonic-mean P/B weighted by look-through position size.</div>
        {formula('P/B = Σwᵢ / Σ(wᵢ / PBᵢ)')}
        <div style={{ color: '#71717a' }}>Measures price paid per dollar of book value across all holdings.</div>
      </>),
      formatted: metrics.blended_pb.value !== null ? `${metrics.blended_pb.value.toFixed(2)}×` : null,
      metric: metrics.blended_pb,
      equityWeight: universe.equity_weight,
    },
    {
      title: 'EV / EBITDA',
      tooltip: (<>
        <div>Harmonic-mean enterprise value to EBITDA; a leverage-neutral valuation multiple.</div>
        {formula('EV/EBITDA = Σwᵢ / Σ(wᵢ / EVEBITDAᵢ)')}
        <div style={{ color: '#71717a' }}>Less distorted by capital structure than P/E; useful for comparing across sectors.</div>
      </>),
      formatted: metrics.ev_to_ebitda.value !== null ? `${metrics.ev_to_ebitda.value.toFixed(1)}×` : null,
      metric: metrics.ev_to_ebitda,
      equityWeight: universe.equity_weight,
    },
    {
      title: 'Dividend Yield',
      tooltip: (<>
        <div>Arithmetic weighted-mean yield across look-through holdings.</div>
        {formula('DY = Σ(wᵢ × DYᵢ)')}
        <div style={{ color: '#71717a' }}>Yield is value-additive; weighted arithmetic mean is appropriate.</div>
      </>),
      formatted: metrics.dividend_yield.value !== null ? `${(metrics.dividend_yield.value * 100).toFixed(2)}%` : null,
      metric: metrics.dividend_yield,
      equityWeight: universe.equity_weight,
    },
  ];

  const qualityTiles: TileProps[] = [
    {
      title: 'Return on Equity',
      tooltip: (<>
        <div>Weighted-mean ROE across look-through holdings.</div>
        {formula('ROE = Σ(wᵢ × ROEᵢ)')}
        <div style={{ color: '#71717a' }}>Net income ÷ shareholder equity. Measures how efficiently the portfolio's companies generate returns.</div>
      </>),
      formatted: metrics.return_on_equity.value !== null ? `${(metrics.return_on_equity.value * 100).toFixed(1)}%` : null,
      metric: metrics.return_on_equity,
      equityWeight: universe.equity_weight,
    },
    {
      title: 'Profit Margin',
      tooltip: (<>
        <div>Weighted-mean net profit margin across look-through holdings.</div>
        {formula('PM = Σ(wᵢ × PMᵢ)')}
        <div style={{ color: '#71717a' }}>Net income ÷ revenue. Higher margins indicate more pricing power or operational efficiency.</div>
      </>),
      formatted: metrics.profit_margin.value !== null ? `${(metrics.profit_margin.value * 100).toFixed(1)}%` : null,
      metric: metrics.profit_margin,
      equityWeight: universe.equity_weight,
    },
    {
      title: 'Debt / Equity',
      tooltip: (<>
        <div>Weighted-mean D/E ratio across look-through holdings.</div>
        {formula('D/E = Σ(wᵢ × DEᵢ)')}
        <div style={{ color: '#71717a' }}>Total debt ÷ shareholder equity. Higher values mean more financial leverage.</div>
      </>),
      formatted: metrics.debt_to_equity.value !== null ? `${metrics.debt_to_equity.value.toFixed(2)}×` : null,
      metric: metrics.debt_to_equity,
      equityWeight: universe.equity_weight,
    },
  ];

  const provenance = (
    <>
      {source} · {asOf} · enriched {universe.resolved} names
      ({pct(universe.resolved_weight)} of equity book) · ≥ {(universe.min_weight_threshold * 10000).toFixed(0)} bps
      {universe.capped ? ` · capped at top ${universe.equity_considered}` : ''}
    </>
  );

  return (
    <>
      {/* Valuation metrics — full width, compact strip */}
      <div style={{ marginTop: '18px' }}>
        <ValuationGridCard
          tiles={[...valuationTiles, ...balanceSheetTiles, ...qualityTiles]}
          provenance={provenance}
        />
      </div>

      {/* Risk metrics + return-correlation row (injected by the parent) */}
      {riskRow && <div style={{ marginTop: '18px' }}>{riskRow}</div>}

      {/* Market cap distribution */}
      <div className="row g-4 mb-4" style={{ marginTop: '18px' }}>
        <div className="col-12">
          <div className="card card-forest">
            <div className="card-body p-3 p-md-4">
              <CardHeading title="Market Cap" />
              {(() => {
                const mc = data.market_cap;
                const covered = mc.covered_weight;
                // Tier boundaries are USD-defined market-cap buckets (kept in USD).
                const tiers = [
                  { label: 'Large cap', sublabel: '> $10B', weight: mc.large_cap_weight },
                  { label: 'Mid cap',   sublabel: '$2–10B', weight: mc.mid_cap_weight },
                  { label: 'Small cap', sublabel: '< $2B',  weight: mc.small_cap_weight },
                ];
                const maxWeight = Math.max(...tiers.map(t => t.weight), 0.001);
                return (
                  <>
                    {/* Stacked bar */}
                    {covered > 0 && (
                      <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', marginBottom: '18px', background: '#f4f4f5' }}>
                        <div style={{ width: `${(mc.large_cap_weight / covered) * 100}%`, background: '#000537', transition: 'width 0.4s ease' }} />
                        <div style={{ width: `${(mc.mid_cap_weight   / covered) * 100}%`, background: '#8c9fd4', transition: 'width 0.4s ease' }} />
                        <div style={{ width: `${(mc.small_cap_weight / covered) * 100}%`, background: '#c4cde8', transition: 'width 0.4s ease' }} />
                      </div>
                    )}
                    {/* Rows */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {tiers.map((tier, i) => {
                        const colors = ['#000537', '#8c9fd4', '#c4cde8'];
                        return (
                          <div key={tier.label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors[i], flexShrink: 0 }} />
                            <span style={{ width: '70px', flexShrink: 0, fontSize: '0.82rem', color: '#52525b' }}>{tier.label}</span>
                            <span style={{ width: '48px', flexShrink: 0, fontSize: '0.75rem', color: '#a1a1aa', whiteSpace: 'nowrap' }}>{tier.sublabel}</span>
                            <div style={{ flex: 1, height: '6px', background: '#f4f4f5', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ width: `${maxWeight > 0 ? (tier.weight / maxWeight) * 100 : 0}%`, height: '100%', background: colors[i], borderRadius: '3px', transition: 'width 0.4s ease' }} />
                            </div>
                            <span style={{ width: '48px', flexShrink: 0, textAlign: 'right', fontSize: '0.82rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>
                              {(tier.weight * 100).toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: '12px', fontSize: '0.75rem', color: '#a1a1aa' }}>
                      Coverage: {pct(mc.covered_weight)} of equity book · weights as % of total portfolio
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Sector breakdown — full width, at the bottom */}
      {data.sector_breakdown.length > 0 && (
        <div className="row g-4" style={{ marginTop: '18px' }}>
          <div className="col-12">
            <SectorBreakdownCard
              rows={data.sector_breakdown}
              portfolioEquityWeight={universe.equity_weight}
              portfolioPE={metrics.blended_pe.value}
              portfolioPB={metrics.blended_pb.value}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ValuationSection;
